import { Head, Link } from '@inertiajs/react'
import { Building2, GraduationCap, UserCheck, Users } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

type Stat = {
  label: string
  value: number
  description: string
  href?: string | null
}

type QuickAction = {
  label: string
  href: string
  tone?: 'primary' | 'secondary'
}

export type AdminDashboardProps = {
  stats: Stat[]
  quickActions: QuickAction[]
  breadcrumbs: Breadcrumb[]
}

const iconMap: Record<number, React.ReactNode> = {
  0: <Building2 className="h-6 w-6 text-brand-200" />,
  1: <Users className="h-6 w-6 text-brand-200" />,
  2: <GraduationCap className="h-6 w-6 text-brand-200" />,
  3: <UserCheck className="h-6 w-6 text-brand-200" />,
  4: <UserCheck className="h-6 w-6 text-brand-200" />,
}

export default function AdminDashboard({ stats, quickActions, breadcrumbs }: AdminDashboardProps) {
  return (
    <AppShell>
      <Head title="管理者ダッシュボード" />
      <PageHeader
        title="管理者ダッシュボード"
        description="Talentbox 全体のサマリーを確認し、主要マスターを管理できます。"
        breadcrumbs={breadcrumbs}
      />

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {stats.map((stat, index) => (
          <Card key={stat.label} className="space-y-4" padding="lg">
            <div className="flex items-center justify-between">
              <div className="rounded-full bg-brand-500/15 p-3">{iconMap[index] ?? iconMap[2]}</div>
              {stat.href ? (
                <Link href={stat.href} className="text-xs text-brand-200 hover:text-brand-100 transition">
                  管理画面へ
                </Link>
              ) : null}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-white/50">{stat.label}</p>
              <p className="mt-2 text-3xl font-semibold text-white">{stat.value.toLocaleString()}</p>
            </div>
            <p className="text-sm text-white/60">{stat.description}</p>
          </Card>
        ))}
      </section>

      <section className="mt-10 space-y-4">
        <h2 className="text-lg font-semibold text-white">クイックアクション</h2>
        <div className="flex flex-wrap gap-4">
          {quickActions.map((action) => (
            <Button
              key={action.label}
              asChild
              variant={action.tone === 'secondary' ? 'secondary' : 'primary'}
            >
              <Link href={action.href}>{action.label}</Link>
            </Button>
          ))}
        </div>
      </section>
    </AppShell>
  )
}
